import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { Close } from "@mui/icons-material";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import UpdateUserModal from "./UpdateUserModal";
import ViewUserModal from "./ViewUserModal";
import AddUserModal from "./AddUserModal";
import {
  useDeleteUserMutation,
  useGetAllUsersQuery,
} from "../../Slices/roomNateApis";
import { useUserProfileSelector } from "../../hooks/useUserProfile";
import UserEngagement from "./UserEngagement";
import { useGlobalContext } from "../../context/GlobalContext";
import AdminPartnerSelect from "../AdminPartnerSelect";

function statusColor(status) {
  switch (status?.toLowerCase()) {
    case "active":
      return "success";
    case "pending":
      return "warning";
    case "inactive":
    case "blocked":
      return "error";
    default:
      return "default";
  }
}

function UserCard({ row, isSelf, onDeleteClick, onViewEngagement }) {
  const displayName = row?.name?.trim() || null;
  const statusLabel = row?.status
    ? row.status.charAt(0).toUpperCase() + row.status.slice(1)
    : "Unknown";

  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ pb: 1, pt: 1.5, px: 2 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            {/* Email */}
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                fontSize: "0.875rem",
                wordBreak: "break-all",
                lineHeight: 1.4,
              }}
            >
              {row?.email}
            </Typography>
            {/* Name */}
            <Typography
              variant="caption"
              color={displayName ? "text.secondary" : "text.disabled"}
              sx={{ mt: 0.25, display: "block" }}
            >
              {displayName || "No name"}
            </Typography>
          </Box>
          {/* Status chip */}
          <Chip
            label={statusLabel}
            size="small"
            color={statusColor(row?.status)}
            variant={row?.status?.toLowerCase() === "active" ? "filled" : "outlined"}
            sx={{ flexShrink: 0, mt: 0.25 }}
          />
        </Stack>
      </CardContent>

      <Divider />

      <CardActions sx={{ px: 1.5, py: 0.75, gap: 0.25 }}>
        <Tooltip title="View Engagements">
          <IconButton size="small" onClick={() => onViewEngagement(row)}>
            <RemoveRedEyeOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <ViewUserModal items={row} />
        <UpdateUserModal items={row} />
        {!isSelf && (
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => onDeleteClick(row._id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
}

export default function UserTable() {
  const { activePartner } = useGlobalContext();
  const { isFetching, data: FetchUsers } = useGetAllUsersQuery(activePartner);
  const { userProfile: userData } = useUserProfileSelector();
  const [deleteUser, resultStats] = useDeleteUserMutation();

  const [open, setOpen] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState("");
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);
  const [selectedUserEngagement, setSelectedUserEngagement] = React.useState(null);

  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const deleteItem = () => {
    handleClose();
    deleteUser({ id: selectedId });
  };

  // Preserve existing alert logic (success/error states as-is)
  React.useEffect(() => {
    if (resultStats?.isSuccess) {
      setErrorAlert(true);
      setTimeout(() => setErrorAlert(false), 2000);
    }
    if (resultStats?.isError) {
      setSuccessAlert(true);
      setTimeout(() => setSuccessAlert(false), 2000);
    }
  }, [resultStats]);

  if (selectedUserEngagement)
    return <UserEngagement user={selectedUserEngagement} />;

  const users = FetchUsers ? [...FetchUsers].reverse() : [];

  return (
    <>
      {successAlert && <SuccessAlert title="User has been deleted successfully!" />}
      {errorAlert && <ErrorAlert title="Something Went Wrong!" />}

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Users{" "}
            {users.length > 0 && (
              <Typography component="span" variant="body2" color="text.secondary">
                ({users.length})
              </Typography>
            )}
          </Typography>
          <Box sx={{ width: { xs: "100%", sm: 280 }, flexShrink: 0 }}>
            <AdminPartnerSelect />
          </Box>
        </Box>
        <Box sx={{ alignSelf: { xs: "flex-start", md: "center" } }}>
          <AddUserModal />
        </Box>
      </Box>

      {/* Single-column list */}
      {isFetching && users.length === 0 ? (
        <Typography color="text.secondary">Loading...</Typography>
      ) : users.length === 0 ? (
        <Typography color="text.secondary">No users found.</Typography>
      ) : (
        <>
          {isFetching && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Refreshing users...
            </Typography>
          )}
          <Stack spacing={1.5} sx={{ width: "100%", alignSelf: "stretch" }}>
            {users.map((row, index) => (
              <UserCard
                key={row._id || index}
                row={row}
                isSelf={userData?._id === row?._id}
                onDeleteClick={handleClickOpen}
                onViewEngagement={setSelectedUserEngagement}
              />
            ))}
          </Stack>
        </>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        maxWidth="sm"
        fullWidth
        open={open}
        aria-labelledby="delete-user-title"
        aria-describedby="delete-user-description"
      >
        <DialogTitle id="delete-user-title">Confirm the Action?</DialogTitle>
        <Box position="absolute" top={10} right={0}>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent>
          <DialogContentText id="delete-user-description">
            Do you really want to delete this user?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={deleteItem} variant="contained" color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
